import { useCallback, useMemo } from 'react';
import { FILTER_CONFIG, FilterKey, getFilterChipLabel } from '@retail/products/utils';
import { useFilters } from '@retail/products/hooks';
import { Stack } from '@mui/material';
import { FilterChip } from './FilterChip';
import { RemoveAllFiltersChip } from './RemoveAllFiltersChip';
import { useAppTFunction } from '@retail/app/i18n';
import { FilterObjectOptions } from '@retail/products/types';

interface GenericFilterChipsProps<K extends FilterKey> {
  allowedKeys: readonly K[];
  filterOptions?: FilterObjectOptions;
}

export const FilterChipContainer = <K extends FilterKey>({
  allowedKeys,
  filterOptions,
}: GenericFilterChipsProps<K>) => {
  const t = useAppTFunction();

  const { filters, setFilters, resetFilters } = useFilters<K>(allowedKeys);

  const handleRemove = useCallback(
    (key: K, valueToDelete?: string) => {
      const currentValue = filters[key];

      if (Array.isArray(currentValue)) {
        const nextList = currentValue.filter((val) => val !== valueToDelete);
        setFilters({ [key]: nextList } as any);
      } else {
        setFilters({ [key]: undefined } as any);
      }
    },
    [filters, setFilters]
  );

  const chips = useMemo(() => {
    const result: { id: string; name: string; onDelete: () => void }[] = [];

    allowedKeys.forEach((key) => {
      const value = filters[key];

      if (value === undefined || value === null || value === '') return;

      const config = FILTER_CONFIG[key];
      const getLabel = (singleVal: any) => getFilterChipLabel(key, singleVal, { filterOptions, t });

      if (config.type === 'list') {
        const listArray = Array.isArray(value) ? value : String(value).split(',').filter(Boolean);

        listArray.forEach((singleVal) => {
          result.push({
            id: `${String(key)}-${singleVal}`,
            name: getLabel ? getLabel(singleVal) : String(singleVal),
            onDelete: () => handleRemove(key, String(singleVal)),
          });
        });
        return;
      }

      if (config.type === 'range') {
        const rangeArray = Array.isArray(value) ? value : String(value).split(',').filter(Boolean);

        if (rangeArray.length === 0) return;

        result.push({
          id: String(key),
          name: getLabel(rangeArray),
          onDelete: () => setFilters({ [key]: undefined } as any),
        });
        return;
      }

      if (config.type === 'boolean') {
        const boolValue = value === true || value === 'true';

        if (boolValue) {
          result.push({
            id: `${String(key)}-true`,
            name: getLabel ? getLabel(true) : String(key),
            onDelete: () => handleRemove(key),
          });
        }
        return;
      }

      if (value === 'UNSPECIFIED') return;

      result.push({
        id: `${String(key)}-${value}`,
        name: getLabel ? getLabel(value) : String(value),
        onDelete: () => handleRemove(key),
      });
    });

    return result;
  }, [allowedKeys, filters, filterOptions, t, handleRemove, setFilters]);

  return (
    <Stack flexDirection="row" gap={1} alignItems="center" flexWrap="wrap">
      {chips.map((chip) => (
        <FilterChip key={chip.id} name={chip.name} onDelete={chip.onDelete} />
      ))}
      {chips.length > 0 && <RemoveAllFiltersChip resetFilters={() => resetFilters()} />}
    </Stack>
  );
};
